/**
 * Popup markup for the community program layers.
 *
 * Pure and separate from the rendering hook so the escaping is testable:
 * this is the only place in the app where unreviewed third party text
 * reaches innerHTML, so a stray quote or angle bracket in an organization
 * name is a realistic failure, not a hypothetical one.
 *
 * A Leaflet popup rather than a sheet on purpose. A popup anchored to a map
 * feature is chrome in the same category as the structure label tooltip,
 * not a dialog, so the "every new sheet is an AdaptiveSheet" rule does not
 * reach it.
 */
import { escapeHtml } from '../../utils/escapeHtml';
import {
  COMMUNITY_PROGRAMS,
  type CommunityProgramId,
  type FireSafeCouncilProperties,
  type FirewiseProperties,
} from './communityPrograms';

function line(text: string, style = ''): string {
  return `<div style="font-size:12px;margin-top:2px;${style}">${escapeHtml(text)}</div>`;
}

function heading(text: string): string {
  return `<div style="font-size:13px;font-weight:700;line-height:1.3;">${escapeHtml(text)}</div>`;
}

function footer(text: string): string {
  return `<div style="font-size:11px;margin-top:6px;opacity:0.65;">${escapeHtml(text)}</div>`;
}

/** "Fawnskin, CA", "San Bernardino County, CA", or "CA". */
function placeLine(
  city: string | null,
  county: string | null,
  state: string | null
): string | null {
  const where = city ?? (county ? `${county} County` : null);
  if (where && state) return `${where}, ${state}`;
  return where ?? state;
}

export function firewisePopupHtml(props: FirewiseProperties): string {
  const program = COMMUNITY_PROGRAMS.firewise;
  const parts = [heading(props.name)];
  const place = placeLine(props.city, props.county, props.state);
  parts.push(line(place ? `${program.singular}, ${place}` : program.singular));
  if (props.approvalYear) parts.push(line(`Recognized since ${props.approvalYear}`));
  const facts: string[] = [];
  if (props.residentCount) facts.push(`${props.residentCount.toLocaleString('en-US')} residents`);
  if (props.totalInvestment) {
    facts.push(`$${Math.round(props.totalInvestment).toLocaleString('en-US')} invested`);
  }
  if (facts.length > 0) parts.push(line(facts.join(' · ')));
  parts.push(footer(program.sourceLabel));
  return parts.join('');
}

export function fireSafeCouncilPopupHtml(props: FireSafeCouncilProperties): string {
  const program = COMMUNITY_PROGRAMS['fire-safe-councils'];
  const parts = [heading(props.name)];
  const kind = [props.orgType, props.orgClass].filter(Boolean).join(' · ');
  if (kind) parts.push(line(kind));
  const place = placeLine(props.city, props.county, null);
  if (place) parts.push(line(place));
  if (props.website) {
    // The export already dropped anything that is not http or https, so the
    // only job left here is escaping.
    const href = escapeHtml(props.website);
    parts.push(
      `<div style="font-size:12px;margin-top:4px;"><a href="${href}" target="_blank" rel="noopener noreferrer">Visit their site</a></div>`
    );
  }
  parts.push(footer(program.sourceLabel));
  return parts.join('');
}

export function communityProgramPopupHtml(
  id: CommunityProgramId,
  props: FirewiseProperties | FireSafeCouncilProperties
): string {
  return id === 'firewise'
    ? firewisePopupHtml(props as FirewiseProperties)
    : fireSafeCouncilPopupHtml(props as FireSafeCouncilProperties);
}
